import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Loader2,
  Save,
  Upload,
  X,
  Building2,
  DollarSign,
  Calendar,
  User
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLandlordStore, ProjectType, ProjectStatus, ProjectPriority } from '@/stores/useLandlordStore';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { useToast } from '@/contexts/ToastContext';

export const EditProject: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { properties, fetchProperties, projects, updateProject, fetchProjectDetails } = useLandlordStore();
  const { success, error: toastError, warning } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    property_id: '',
    name: '',
    description: '',
    type: 'Renovation' as ProjectType,
    status: 'Planned' as ProjectStatus,
    priority: 'Medium' as ProjectPriority,
    budget_estimated: '',
    budget_actual: '0',
    start_date: '',
    end_date: '',
    notes: '',
    contractor_name: '',
    contractor_phone: '',
    contractor_email: '',
    contractor_company: ''
  });

  const [photos, setPhotos] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadProject = async () => {
      if (properties.length === 0) {
        await fetchProperties();
      }

      if (projectId) {
        try {
          // First try to find in existing projects
          let project = projects.find(p => p.id === projectId);
          
          if (!project) {
            // If not found in local state, try to fetch and then search again
            await fetchProjectDetails(projectId);
            // After fetching, the project should be in the store, but we need to wait
            // For now, show error if not found
            warning('Project not found', 'The project you are looking for does not exist.');
            navigate('/landlord/projects');
            return;
          }

          setFormData({
            property_id: project.property_id || '',
            name: project.name || '',
            description: project.description || '',
            type: project.type || 'Renovation',
            status: project.status || 'Planned',
            priority: project.priority || 'Medium',
            budget_estimated: project.budget_estimated?.toString() || '0',
            budget_actual: project.budget_actual?.toString() || '0',
            start_date: project.start_date ? new Date(project.start_date).toISOString().split('T')[0] : '',
            end_date: project.end_date ? new Date(project.end_date).toISOString().split('T')[0] : '',
            notes: project.notes || '',
            contractor_name: project.contractor?.name || '',
            contractor_phone: project.contractor?.phone || '',
            contractor_email: project.contractor?.email || '',
            contractor_company: project.contractor?.company || ''
          });
          setPhotos(project.photos?.map(p => p.url) || []);
        } catch (error) {
          console.error('Failed to load project:', error);
          toastError('Error', 'Failed to load project details.');
          navigate('/landlord/projects');
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadProject();
  }, [projectId, projects, fetchProperties, fetchProjectDetails, navigate, warning, toastError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newPhotos = Array.from(e.target.files).map(file => URL.createObjectURL(file));
      setPhotos(prev => [...prev, ...newPhotos]);
    }
  };

  const removePhoto = (uri: string) => {
    setPhotos(photos.filter(photo => photo !== uri));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.property_id) newErrors.property_id = 'Property is required';
    if (!formData.name.trim()) newErrors.name = 'Project name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.budget_estimated || parseFloat(formData.budget_estimated) <= 0) {
      newErrors.budget_estimated = 'Estimated budget must be greater than 0';
    }
    if (!formData.start_date) newErrors.start_date = 'Start date is required';
    if (formData.end_date && new Date(formData.end_date) < new Date(formData.start_date)) {
      newErrors.end_date = 'End date must be after start date';
    }
    if (formData.contractor_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contractor_email)) {
      newErrors.contractor_email = 'Invalid email format';
    }
    if (formData.contractor_phone && !/^[\d\s\-\+\(\)]+$/.test(formData.contractor_phone)) {
      newErrors.contractor_phone = 'Invalid phone number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      warning('Validation Error', 'Please correct the errors in the form.');
      return;
    }

    if (!projectId) return;

    setIsSubmitting(true);

    try {
      const projectData = {
        property_id: formData.property_id,
        name: formData.name,
        description: formData.description,
        type: formData.type,
        status: formData.status,
        priority: formData.priority,
        budget_estimated: parseFloat(formData.budget_estimated),
        budget_actual: parseFloat(formData.budget_actual) || 0,
        start_date: formData.start_date,
        end_date: formData.end_date || undefined,
        notes: formData.notes,
        contractor: {
          id: crypto.randomUUID(),
          name: formData.contractor_name,
          phone: formData.contractor_phone,
          email: formData.contractor_email || undefined,
          company: formData.contractor_company || undefined
        }
      };

      await updateProject(projectId, projectData);
      success('Project updated!', 'Your capital project has been updated successfully.');
      navigate('/landlord/projects');
    } catch (error) {
      console.error('Failed to update project:', error);
      toastError('Update failed', 'Failed to update project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  const inputClass = (fieldName: string) =>
    `w-full px-4 py-3 bg-light-card dark:bg-dark-card border ${
      errors[fieldName] 
        ? 'border-red-500 focus:ring-red-500/50' 
        : 'border-light-border dark:border-dark-border focus:ring-primary/50'
    } rounded-xl text-light-text dark:text-dark-text placeholder:text-light-subtle dark:text-dark-subtle focus:outline-none focus:ring-2 transition-shadow`;

  const labelClass = "block text-sm font-semibold text-light-text dark:text-dark-text mb-2";

  return (
    <div className="max-w-4xl mx-auto pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
        >
          <ArrowLeft size={24} className="text-primary dark:text-golden" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-light-text dark:text-dark-text">
            Edit Project
          </h1>
          <p className="text-sm text-light-subtle dark:text-dark-subtle">
            Update capital project details
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-light-card dark:bg-dark-card p-6 md:p-8 rounded-xl shadow-sm border border-light-border dark:border-dark-border space-y-8">
          
          {/* Basic Information */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Property *</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                  <select
                    name="property_id"
                    value={formData.property_id}
                    onChange={handleInputChange}
                    className={`${inputClass('property_id')} pl-10`}
                  >
                    <option value="">Select Property</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.property_id && <p className="text-red-500 text-xs mt-1">{errors.property_id}</p>}
              </div>

              <div>
                <label className={labelClass}>Project Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Kitchen Renovation"
                  className={inputClass('name')}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Project Type *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className={inputClass('type')}
                >
                  <option value="Renovation">Renovation</option>
                  <option value="Repair">Repair</option>
                  <option value="Construction">Construction</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Improvement">Improvement</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Status *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className={inputClass('status')}
                >
                  <option value="Planned">Planned</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Priority *</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className={inputClass('priority')}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe the scope of work..."
                rows={4}
                className={inputClass('description')}
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>
          </div>

          {/* Budget & Timeline */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">
              Budget & Timeline
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Estimated Budget (KES) *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                  <input
                    type="number"
                    name="budget_estimated"
                    value={formData.budget_estimated}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className={inputClass('budget_estimated')}
                  />
                </div>
                {errors.budget_estimated && <p className="text-red-500 text-xs mt-1">{errors.budget_estimated}</p>}
              </div>

              <div>
                <label className={labelClass}>Current Actual Spend (KES)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                  <input
                    type="number"
                    name="budget_actual"
                    value={formData.budget_actual}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className={inputClass('budget_actual')}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Start Date *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    className={inputClass('start_date')}
                  />
                </div>
                {errors.start_date && <p className="text-red-500 text-xs mt-1">{errors.start_date}</p>}
              </div>

              <div>
                <label className={labelClass}>Expected Completion Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    className={inputClass('end_date')}
                  />
                </div>
                {errors.end_date && <p className="text-red-500 text-xs mt-1">{errors.end_date}</p>}
              </div>
            </div>
          </div>

          {/* Contractor Information */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">
              Contractor Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Contractor Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-light-subtle dark:text-dark-subtle" size={18} />
                  <input
                    type="text"
                    name="contractor_name"
                    value={formData.contractor_name}
                    onChange={handleInputChange}
                    placeholder="Contractor name"
                    className={inputClass('contractor_name')}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Company Name</label>
                <input
                  type="text"
                  name="contractor_company"
                  value={formData.contractor_company}
                  onChange={handleInputChange}
                  placeholder="Company name"
                  className={inputClass('contractor_company')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Phone Number</label>
                <input
                  type="tel"
                  name="contractor_phone"
                  value={formData.contractor_phone}
                  onChange={handleInputChange}
                  placeholder="+254 700 000 000"
                  className={inputClass('contractor_phone')}
                />
                {errors.contractor_phone && <p className="text-red-500 text-xs mt-1">{errors.contractor_phone}</p>}
              </div>

              <div>
                <label className={labelClass}>Email Address</label>
                <input
                  type="email"
                  name="contractor_email"
                  value={formData.contractor_email}
                  onChange={handleInputChange}
                  placeholder="contractor@example.com"
                  className={inputClass('contractor_email')}
                />
                {errors.contractor_email && <p className="text-red-500 text-xs mt-1">{errors.contractor_email}</p>}
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">
              Additional Notes
            </h2>
            <div>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Any additional notes or important information..."
                rows={3}
                className={inputClass('notes')}
              />
            </div>
          </div>

          {/* Existing Photos */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">
              Project Photos
            </h2>
            <div className="flex flex-wrap gap-4">
              {photos.map((uri) => (
                <div
                  key={uri}
                  className="relative w-24 h-24 rounded-lg overflow-hidden border border-light-border dark:border-dark-border"
                >
                  <img
                    src={uri}
                    alt="preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(uri)}
                    className="absolute top-1 right-1 bg-black/60 rounded-full p-1"
                  >
                    <X size={14} className="text-white" />
                  </button>
                </div>
              ))}

              <label className="flex flex-col items-center justify-center w-24 h-24 rounded-lg border-2 border-dashed border-light-border dark:border-dark-border cursor-pointer hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
                <Upload size={24} className="text-light-subtle dark:text-dark-subtle mb-1" />
                <span className="text-[10px] text-light-subtle dark:text-dark-subtle font-medium uppercase text-center leading-tight">Add<br />Photos</span>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoChange}
                />
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pt-4 flex gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 py-3 text-center bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-xl font-bold text-base hover:opacity-90 transition-opacity"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-3 text-center bg-primary text-white rounded-xl font-bold text-base hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center gap-2"
            >
              {isSubmitting && <Loader2 className="animate-spin" size={20} />}
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};